import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PermissionService } from '../../security/permission.service';
import { AuditOverdueFindingReportRow } from '../audit.models';
import { AuditService } from '../audit.service';
import { auditNavItems, formatAuditDate, statusSeverity } from '../audit-ui';

@Component({
  selector: 'app-audit-overdue-findings-report',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    ProgressBarModule,
    TableModule,
    TagModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  template: `
    @if (loading) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }
    <app-page-shell>
      <app-page-hero eyebrow="Audit Reports" title="Overdue Findings Report" subtitle="Overdue findings with linked corrective action aging.">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" severity="secondary" [outlined]="true"></a>
          }
        </div>
        <div actions>
          <a pButton routerLink="/audit/findings" label="Findings" icon="pi pi-exclamation-triangle" severity="secondary" [outlined]="true"></a>
          <button pButton type="button" icon="pi pi-refresh" label="Refresh" severity="secondary" [outlined]="true" (click)="load()"></button>
        </div>
      </app-page-hero>

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-page-data-panel title="Overdue Findings" subtitle="Findings whose due date has passed and are not closed." [resultText]="rows.length + ' rows'">
        <p-table [value]="rows" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1120px' }">
          <ng-template pTemplate="header">
            <tr>
              <th>Finding</th><th>Case</th><th>Severity</th><th>Status</th><th>Owner</th><th>Due</th><th>Actions</th><th>Source</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td><a [routerLink]="['/audit/findings', row.findingId]">{{ row.findingNumber || row.title || '-' }}</a></td>
              <td><a [routerLink]="['/audit/cases', row.auditCaseId]">{{ row.auditNumber || '-' }}</a></td>
              <td><p-tag [value]="row.severity || '-'" [severity]="statusSeverity(row.severity)"></p-tag></td>
              <td><p-tag [value]="row.status || '-'" [severity]="statusSeverity(row.status)"></p-tag></td>
              <td>{{ row.ownerUserId || '-' }}</td>
              <td>{{ formatDate(row.dueAt) }} <strong class="danger">({{ row.daysOverdue || 0 }}d)</strong></td>
              <td>{{ row.openActionCount || 0 }} open / {{ row.overdueActionCount || 0 }} overdue</td>
              <td>{{ row.sourceResourceNumber || row.sourceResourceType || row.sourceModule || '-' }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="8" class="text-center p-4">No overdue findings are available.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>
  `,
  styles: [`.danger{color:#b91c1c}`]
})
export class AuditOverdueFindingsReportComponent implements OnInit {
  private readonly auditService = inject(AuditService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  rows: AuditOverdueFindingReportRow[] = [];
  readonly visibleNavItems = auditNavItems.filter(item => this.permissionService.hasPermission(item.permission));
  readonly statusSeverity = statusSeverity;
  readonly formatDate = formatAuditDate;

  ngOnInit(): void {
    this.load();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Overdue Findings', value: this.rows.length, tone: 'danger' },
      { label: 'Critical', value: this.rows.filter(row => row.severity === 'CRITICAL').length, tone: 'danger' },
      { label: 'Open Actions', value: this.rows.reduce((sum, row) => sum + (row.openActionCount || 0), 0), tone: 'warn' },
      { label: 'Overdue Actions', value: this.rows.reduce((sum, row) => sum + (row.overdueActionCount || 0), 0), tone: 'danger' }
    ];
  }

  load(): void {
    this.loading = true;
    this.auditService.overdueFindingsReport().subscribe({
      next: response => this.rows = response.data ?? [],
      error: () => this.rows = [],
      complete: () => this.loading = false
    });
  }
}
